import {
  toWSDateString,
  WSFilter,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import {
  Batch,
  BatchStatus,
  SearchRowAutoPayStrategy,
  CustomField,
  Engagement,
  RequirementDefinition,
  RequirementExpirationStrategy,
  RequirementReviewPolicy,
  RequirementType,
  SearchRowTinStatus
} from "@wingspanhq/wingspan-api-sdk";
import { getRequirementDefinitionName } from "../../../../RequirementDefinitions/utils";
import { customFieldsToFilters } from "../../../utils";
import {
  PayeeFilterEligibilityStatus,
  PayeeFilterRequirementStatus,
  PayeeFilters,
  PayeeFilterShareTaxDocument
} from "./types";

type Data = {
  engagements: Engagement[];
  bulkBatches: Batch[];
  customFields: CustomField[];
  requirements: RequirementDefinition[];
};

function getRequirementStatusOptions(requirement: RequirementDefinition) {
  const hasReviewStrategy =
    requirement.reviewPolicy === RequirementReviewPolicy.ALWAYS_REQUIRED ||
    requirement.reviewPolicy === RequirementReviewPolicy.ON_FAIL;

  const hasExpirationStrategy =
    requirement.expirationStrategy ===
    RequirementExpirationStrategy.DAYS_AFTER_COMPLETE;

  const options: WSFilterOption<"radio">[] = [
    {
      name: PayeeFilterRequirementStatus.All,
      label: "All",
      helperText: "All contractors assigned this requirement",
      hidePill: true
    }
  ];

  switch (requirement.type) {
    case RequirementType.REGISTRATION:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor's sign up",
        group: "Incomplete"
      });
      break;

    case RequirementType.TAX_VERIFICATION:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor's verification",
        group: "Incomplete"
      });
      break;

    case RequirementType.PAYOUT_METHOD:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor's payout method",
        group: "Incomplete"
      });
      break;

    case RequirementType.SIGNATURE:
      options.push({
        name: PayeeFilterRequirementStatus.AwaitingContractorSignature,
        label: "Awaiting contractor signature",
        group: "Incomplete"
      });

      options.push({
        name: PayeeFilterRequirementStatus.AwaitingClientSignature,
        label: "Awaiting your signature",
        helperText: "Only applicable to countersignature required",
        group: "Incomplete"
      });

      break;
    case RequirementType.DOCUMENT_UPLOAD:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor upload",
        group: "Incomplete"
      });
      break;

    case RequirementType.BIOMETRIC_IDENTITY_VERIFICATION:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor completion",
        group: "Incomplete"
      });
      break;
    case RequirementType.BACKGROUND_CHECK:
      options.push({
        name: PayeeFilterRequirementStatus.PendingBackgroundCheck,
        label: "Awaiting background check completion",
        group: "Incomplete"
      });
      options.push({
        name: PayeeFilterRequirementStatus.ProcessingBackgroundCheck,
        label: "Processing",
        helperText: "Contractor submission is awaiting results",
        group: "Incomplete"
      });
      options.push({
        name: PayeeFilterRequirementStatus.FailedBackgroundCheck,
        label: "Failed background check",
        helperText: "Contractor submission failed and requires your attention",
        group: "Incomplete"
      });
      break;
    case RequirementType.INSURANCE_COVERAGE:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting insurance coverage upload",
        group: "Incomplete"
      });
      break;
  }

  if (hasReviewStrategy) {
    options.push({
      name: PayeeFilterRequirementStatus.PendingApproval,
      label: "Awaiting your approval",
      helperText: "Contractor submission is awaiting your approval",
      group: "Incomplete"
    });

    options.push({
      name: PayeeFilterRequirementStatus.Rejected,
      label: "Rejected",
      helperText:
        "Contractor submission was rejected or marked incomplete by you",
      group: "Incomplete"
    });
  }

  if (hasExpirationStrategy) {
    options.push({
      name: PayeeFilterRequirementStatus.Expired,
      label: "Expired",
      helperText: "Contractor requirement has expired",
      group: "Incomplete"
    });
  }

  options.push({
    name: PayeeFilterRequirementStatus.Complete,
    label: "Complete",
    helperText: "Contractors with this requirement complete",
    group: "Complete"
  });

  if (hasReviewStrategy) {
    options.push({
      name: PayeeFilterRequirementStatus.Approved,
      label: "Approved",
      helperText:
        "Contractor submission was approved or marked complete by you",
      group: "Complete"
    });
  }

  return options;
}

function getVersionOptions(requirement: RequirementDefinition) {
  const versionHistory: Array<{ version?: number; name?: string }> =
    requirement.versionHistory ?? [];
  const currentVersion = requirement.version;

  const currentOption = {
    name: String(currentVersion),
    label: `v${currentVersion}`,
    helperText: `Current version`
  };

  const historyOptions = versionHistory
    .filter(v => v.version !== currentVersion)
    .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
    .map(v => ({
      name: String(v.version),
      label: `v${v.version}`
    }));

  return [currentOption, ...historyOptions];
}

function getRequirementSubFilters(
  requirements: RequirementDefinition[],
  requirementDefinitionId?: string
) {
  const requirementOptions = requirements.map(requirement => ({
    name: requirement.id!,
    label: requirement.name!,
    helperText: getRequirementDefinitionName({
      type: requirement.type!,
      // TODO(ivanh): add context and policy type here
      ...(requirement as any).dataSourceMetadata
    })
  }));

  const filters: WSFilter[] = [
    {
      title: "Requirement",
      titleInLabel: true,
      type: "select",
      name: "requirementDefinitionId",
      options: requirementOptions
    }
  ];

  const requirement = requirements.find(
    requirement => requirement.id === requirementDefinitionId
  );

  if (requirement) {
    const versionOptions = getVersionOptions(requirement);

    if (versionOptions.length > 1) {
      filters.push({
        title: "Requirement version",
        type: "select",
        name: "requirementDefinitionVersion",
        options: versionOptions
      });
    }

    filters.push({
      title: "Requirement status",
      type: "radio",
      name: "requirementStatus",
      options: getRequirementStatusOptions(requirement)
    });
  }

  return filters;
}

export function buildConfig({
  engagements,
  bulkBatches,
  customFields,
  requirements
}: Data) {
  return function (values: Partial<PayeeFilters>) {
    const config: WSFilter[] = [];

    // The v1 "Organizations" filter is gone. `request.ts` never carried
    // `payerId` across — v3 `searchPayeeRows` has no such row filter; a child
    // account is targeted through the `X-Wingspan-Account` scope header — so the
    // control was inert, and the org list feeding it came from v1
    // `usersService.organization.users`, which throws `NotMigrated`.
    // See V3_MIGRATION_TRACKER.md (payee-rows §).

    if (engagements.length > 0) {
      const engagementOptions = engagements.map(engagement => ({
        label: engagement.name ?? "",
        name: engagement.id ?? ""
      }));

      config.push({
        title: "Engagement",
        name: "engagementId",
        type: "select",
        options: engagementOptions
      });
    }

    if (requirements.length > 0) {
      config.push({
        title: "Requirement status",
        type: "radio",
        name: "eligibilityStatus",
        options: [
          {
            name: PayeeFilterEligibilityStatus.All,
            label: "All requirements",
            helperText: "Contractors with assigned requirements",
            hidePill: true
          },
          {
            name: PayeeFilterEligibilityStatus.Complete,
            label: "All requirements complete",
            helperText: "Contractors with all requirements complete"
          },
          {
            name: PayeeFilterEligibilityStatus.Incomplete,
            label: "Has incomplete requirements",
            helperText: "Contractors with any requirements incomplete"
          },
          {
            name: PayeeFilterEligibilityStatus.SpecificRequirement,
            label: "Filter by specific requirement",
            helperText:
              "Contractors with specific requirement complete or incomplete",
            hidePill: true,
            subFilters: getRequirementSubFilters(
              requirements,
              values.requirementDefinitionId
            )
          }
        ]
      });
    }

    config.push({
      title: "Tax ID status",
      name: "searchableTinStatus",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "Not provided",
          helperText: "Tax ID has not been provided",
          name: SearchRowTinStatus.UNVERIFIED
        },
        {
          label: "Mismatch",
          helperText: "Tax ID and name is mismatched",
          name: SearchRowTinStatus.FAILED
        },
        {
          label: "Pending",
          helperText: "Tax ID is being verified",
          name: SearchRowTinStatus.PENDING
        },
        {
          label: "Verified",
          helperText: "Tax ID has been verified",
          name: SearchRowTinStatus.VERIFIED
        }
      ]
    });

    config.push({
      title: "Tax info",
      name: "shareTaxDocument",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "Not shared",
          name: PayeeFilterShareTaxDocument.NotShared
        },
        {
          label: "Shared",
          name: PayeeFilterShareTaxDocument.Shared
        }
      ]
    });

    config.push({
      title: "Autopay",
      name: "autoPayStrategy",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "On",
          name: SearchRowAutoPayStrategy.ALL
        },
        {
          label: "Off",
          name: SearchRowAutoPayStrategy.NONE
        }
      ]
    });

    if (customFields.length > 0) {
      config.push(...customFieldsToFilters(customFields));
    }

    if (bulkBatches.length > 0) {
      // v3 `Batch` replaces the v1 bulk-collaborator batch. `metadata.name`
      // carries the uploaded filename the v1 `getUploadedFilename` helper
      // reconstructed, and `Complete`/`Failed` are the v3 spellings of v1
      // `Complete`/`Failed`.
      const bulkBatchOptions = bulkBatches
        .filter(
          bulkBatch =>
            bulkBatch.status === BatchStatus.COMPLETED ||
            bulkBatch.status === BatchStatus.FAILED
        )
        .map(bulkBatch => ({
          label:
            (bulkBatch.metadata?.name || "Contractor import") +
            ` (created ${toWSDateString(bulkBatch.events?.createdAt)})`,
          name: bulkBatch.id as string
        }));

      config.push({
        title: "Bulk import batch",
        name: "bulkBatchId",
        type: "select",
        options: bulkBatchOptions
      });
    }

    return config;
  };
}

export function getInterceptFiltersChange(
  requirements: RequirementDefinition[]
) {
  return function (prevValue: PayeeFilters, newValue: PayeeFilters) {
    if (
      newValue.eligibilityStatus ===
      PayeeFilterEligibilityStatus.SpecificRequirement
    ) {
      if (
        prevValue.requirementDefinitionId &&
        !newValue.requirementDefinitionId
      ) {
        return {
          ...newValue,
          eligibilityStatus: undefined,
          requirementStatus: undefined
        };
      } else if (
        !prevValue.requirementDefinitionId &&
        !newValue.requirementDefinitionId
      ) {
        return {
          ...newValue,
          requirementDefinitionId: requirements[0]?.id
        };
      }
    }
    return newValue;
  };
}
